import { Add } from "@mui/icons-material";
import { Box, Button, Stack } from "@mui/joy";
import { useNavigate } from "@tanstack/react-router";
import { ApiChallenge } from "griptonite-api-client/types/challenges";
import { groupBy, orderBy } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";

import { useChallengeQuery, useVenueChallengesQuery } from "@/hooks/challenges";
import { useCurrentVenueUuid } from "@/hooks/venues";

import { ChallengeCard, getChallengeStatusLabel } from "../ChallengeCard";
import { PillsSelect } from "../forms/PillsSelect";

const ChallengesList = ({
  activeChallengeUuid,
  isCreating,
}: {
  activeChallengeUuid?: string;
  isCreating?: boolean;
}) => {
  const currentVenueUuid = useCurrentVenueUuid();
  const { data: challenge } = useChallengeQuery(activeChallengeUuid, currentVenueUuid);
  const { data: challenges, isPending: challengesIsPending } =
    useVenueChallengesQuery(currentVenueUuid);

  const stackRef = useRef<HTMLDivElement>(null);

  const itemsByStatus = useMemo(() => {
    const result = groupBy(challenges, "status");

    Object.keys(result).forEach((status) => {
      result[status] = orderBy(result[status], (item) => item.startTime, ["desc"]);
    });
    return result;
  }, [challenges]);

  const [selectedStatus, setSelectedStatus] = useState<ApiChallenge["status"] | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!selectedStatus) {
      if (challenge) {
        setSelectedStatus(challenge.status);
      } else if (isCreating) {
        setSelectedStatus("UPCOMING");
      } else if (itemsByStatus) {
        setSelectedStatus(Object.keys(itemsByStatus)[0] as ApiChallenge["status"]);
      }
    }
  }, [selectedStatus, itemsByStatus, challenge, isCreating]);

  useEffect(() => {
    if (!activeChallengeUuid) {
      if (isCreating) {
        // Scroll to the bottom
        stackRef.current?.scrollTo({
          top: stackRef.current.scrollHeight,
        });
      }
      return;
    }
    if (challengesIsPending) {
      return;
    }
    const el: HTMLElement | undefined | null = stackRef.current?.querySelector(
      `[data-uuid="${activeChallengeUuid}"]`,
    );
    if (!el) {
      // Wait for render
      setTimeout(() => {
        const el: HTMLElement | undefined | null = stackRef.current?.querySelector(
          `[data-uuid="${activeChallengeUuid}"]`,
        );
        if (!el) {
          return;
        }
        stackRef.current?.scrollTo({
          top: el.offsetTop - stackRef.current.offsetTop - 20,
        });
      }, 0);
      return;
    }
    stackRef.current?.scrollTo({
      top: el.offsetTop - stackRef.current.offsetTop - 20,
    });
  }, [activeChallengeUuid, isCreating, challengesIsPending]);

  const navigate = useNavigate();

  const selectPillsOptions = itemsByStatus
    ? Object.keys(itemsByStatus).map((status) => ({
        value: status,
        name: getChallengeStatusLabel(status as ApiChallenge["status"]),
      }))
    : [];

  if (isCreating && !itemsByStatus["UPCOMING"]) {
    selectPillsOptions.push({
      value: "UPCOMING",
      name: "Upcoming",
    });
  }

  return (
    <Stack sx={{ width: 360, minWidth: 360 }}>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          padding: 1,
          zIndex: 10,
          backgroundColor: "white",
        }}
      >
        <PillsSelect
          options={selectPillsOptions}
          value={selectedStatus ?? null}
          onChange={(value) => value && setSelectedStatus(value as ApiChallenge["status"])}
        />
      </Box>
      <Stack
        gap={1}
        sx={{ paddingX: 1, paddingBottom: 1, flex: 1, overflowY: "auto" }}
        ref={stackRef}
      >
        {itemsByStatus?.[selectedStatus || ""]?.map((item) => (
          <ChallengeCard key={item.uuid} challenge={item} />
        ))}
        {selectedStatus === "UPCOMING" && isCreating ? <ChallengeCard /> : null}
      </Stack>
      <Box sx={{ margin: 1, display: "flex" }}>
        <Button
          sx={{ flex: 1 }}
          startDecorator={<Add />}
          onClick={() => {
            navigate({
              to: "/gym/$venueUuid/challenges/create",
              params: { venueUuid: currentVenueUuid! },
            });
          }}
        >
          Create Challenge
        </Button>
      </Box>
    </Stack>
  );
};

export default ChallengesList;
